import { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  Select,
  Button,
  InputNumber,
  Typography,
  Divider,
  Input,
  Alert,
  DatePicker,
  Steps,
  Modal,
  message,
} from 'antd';
import {
  ClockCircleOutlined,
  PlusOutlined,
  EditOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import locale from 'antd/es/date-picker/locale/fr_FR';

dayjs.locale('fr');
import { useAuth } from '../../AuthContext';
import MessageEditorDrawer from './MessageEditorDrawer';

const { Text } = Typography;
const API_URL = process.env.REACT_APP_API_URL;

export default function GestionBroadcastDrawer({
  open,
  onClose,
  onCreated,
  playlist,
  playlists: allPlaylists, // liste complète pour le Select playlist
  messages: userMessages,
  duplicateFrom, // broadcast object for duplication mode
  onMessagesChange,
  userSignature,
  onSignatureUpdated,
}) {
  const { user } = useAuth();
  const [selectedPlaylistId, setSelectedPlaylistId] = useState(null);
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [providers, setProviders] = useState([]);
  const [broadcastName, setBroadcastName] = useState('');
  const [warmupStart, setWarmupStart] = useState(100);
  const [warmupFactor, setWarmupFactor] = useState(1.1);
  const [warmupMax, setWarmupMax] = useState(3960);
  const [estimatedDays, setEstimatedDays] = useState(0);
  const [contactCount, setContactCount] = useState(0);
  const [creating, setCreating] = useState(false);
  const [scheduledAt, setScheduledAt] = useState(null);
  const [step, setStep] = useState(0); // 0 = Diffusion, 1 = Message
  const [messageMode, setMessageMode] = useState('browse'); // 'browse' | 'edit' | 'new'
  const messageEditorActionsRef = useRef(null);

  const token = localStorage.getItem('prae.jwt');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  // Load providers
  useEffect(() => {
    if (!open) return;
    fetch(`${API_URL}/smtp-providers`, { headers })
      .then(r => r.json())
      .then(data => {
        const active = (Array.isArray(data) ? data : []).filter(
          p => !p.deleted_at
        );
        setProviders(active);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Init wizard state à chaque ouverture du drawer
  useEffect(() => {
    if (!open) return;
    setStep(0);
    setMessageMode('browse');
    setContactCount(0);
    setScheduledAt(null);
    setWarmupStart(100);
    setWarmupFactor(1.1);
    setWarmupMax(3960);
    setEstimatedDays(0);

    // Pré-sélection de la playlist (depuis la prop si fournie)
    setSelectedPlaylistId(playlist?.id || null);

    // Pre-fill from duplication
    const DEFAULT_GESTION_PROVIDER_ID = 333;
    if (duplicateFrom) {
      setSelectedProviderId(duplicateFrom.smtp_provider_id);
      if (duplicateFrom.message_id) {
        setSelectedMessageId(duplicateFrom.message_id);
      }
      setBroadcastName(`${duplicateFrom.name} (copie)`);
    } else {
      setSelectedMessageId(null);
      setSelectedProviderId(DEFAULT_GESTION_PROVIDER_ID);
      setBroadcastName(playlist?.name || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, playlist]);

  // Re-fetch contact count + warmup quand la playlist sélectionnée change
  useEffect(() => {
    if (!open || !selectedPlaylistId) {
      setContactCount(0);
      return;
    }
    const pl =
      (allPlaylists || []).find(p => p.id === selectedPlaylistId) ||
      (playlist?.id === selectedPlaylistId ? playlist : null);
    if (!pl) return;

    fetch(`${API_URL}/playlists/gestion/count`, {
      method: 'POST',
      headers,
      body: JSON.stringify(
        typeof pl.gestion_filters === 'string'
          ? JSON.parse(pl.gestion_filters)
          : pl.gestion_filters || {}
      ),
    })
      .then(r => r.json())
      .then(data => {
        const count = data.count || 0;
        setContactCount(count);
        return fetch(
          `${API_URL}/gestion-broadcasts/suggest-warmup?count=${count}`,
          { headers }
        );
      })
      .then(r => r.json())
      .then(data => {
        setWarmupStart(data.start || 100);
        setWarmupFactor(data.factor || 1.1);
        setWarmupMax(data.max || 3960);
        setEstimatedDays(data.estimated_days || 1);
      })
      .catch(() => {});

    // Mettre à jour le nom du broadcast par défaut
    if (!duplicateFrom && !broadcastName) {
      setBroadcastName(pl.name || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedPlaylistId]);

  // Recalculate estimated days when warmup params change
  useEffect(() => {
    if (contactCount <= 0) return;
    fetch(
      `${API_URL}/gestion-broadcasts/suggest-warmup?count=${contactCount}`,
      { headers }
    )
      .then(r => r.json())
      .then(() => {
        // Calculate locally
        let remaining = contactCount;
        let daily = warmupStart;
        let days = 0;
        while (remaining > 0 && days < 365) {
          days++;
          remaining -= Math.min(daily, warmupMax);
          daily = Math.ceil(daily * warmupFactor);
        }
        setEstimatedDays(days);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warmupStart, warmupFactor, warmupMax, contactCount]);

  const handleCreate = async () => {
    if (!selectedPlaylistId) {
      message.warning('Choisissez une playlist');
      return;
    }
    if (!selectedMessageId) {
      message.warning('Choisissez un message');
      return;
    }
    if (!selectedProviderId) {
      message.warning('Choisissez un provider SMTP');
      return;
    }
    const selectedPl =
      (allPlaylists || []).find(p => p.id === selectedPlaylistId) ||
      (playlist?.id === selectedPlaylistId ? playlist : null);
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/gestion-broadcasts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          playlistId: selectedPlaylistId,
          broadcastName: broadcastName.trim() || selectedPl?.name || 'Campagne',
          messageId: selectedMessageId,
          userId: user?.id,
          smtp_provider_id: selectedProviderId,
          warmup_params: {
            start: warmupStart,
            factor: warmupFactor,
            max: warmupMax,
          },
          ...(scheduledAt
            ? { scheduled_start_at: scheduledAt.toISOString() }
            : {}),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const json = await res.json();
      message.success(
        scheduledAt
          ? `Campagne programmée pour le ${scheduledAt.format('D MMMM YYYY')} — la liste sera figée au lancement`
          : `Campagne créée en préparation — la liste sera figée au lancement`
      );
      onCreated?.({ playlistId: selectedPlaylistId, broadcastId: json?.id });
      onClose();
    } catch (err) {
      message.error(`Erreur : ${err.message}`);
    } finally {
      setCreating(false);
    }
  };

  // Group providers by server
  const providerGroups = {};
  providers.forEach(p => {
    const server = p.server || 'Autre';
    if (!providerGroups[server]) providerGroups[server] = [];
    providerGroups[server].push(p);
  });

  const editingMsg =
    messageMode === 'edit'
      ? (userMessages || []).find(m => m.id === selectedMessageId) || null
      : null;

  // Étape 0 (Diffusion) : doit avoir une playlist + un provider pour passer à l'étape 2
  const canGoNext = !!selectedPlaylistId && !!selectedProviderId;
  const selectedPlaylistObj =
    (allPlaylists || []).find(p => p.id === selectedPlaylistId) ||
    (playlist?.id === selectedPlaylistId ? playlist : null);

  const handleClose = () => {
    Modal.confirm({
      title: 'Fermer sans enregistrer ?',
      content: 'Les informations saisies seront perdues.',
      okText: 'Fermer quand même',
      cancelText: "Continuer l'édition",
      okButtonProps: { danger: true },
      onOk: onClose,
    });
  };

  return (
    <Drawer
      title={`Campagne${
        selectedPlaylistObj?.name ? ' : ' + selectedPlaylistObj.name : ''
      } — Étape ${step + 1}/2 : ${step === 0 ? 'Diffusion' : 'Message'}`}
      open={open}
      onClose={handleClose}
      width={640}
      extra={
        step === 0 ? (
          <Button
            type="primary"
            disabled={!canGoNext}
            onClick={() => setStep(1)}
            style={{ background: '#f97316', borderColor: '#f97316' }}
          >
            Suivant
          </Button>
        ) : messageMode !== 'browse' ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => setMessageMode('browse')}>Annuler</Button>
            <Button
              type="primary"
              onClick={() => messageEditorActionsRef.current?.save()}
              style={{ background: '#f97316', borderColor: '#f97316' }}
            >
              {editingMsg ? 'Mettre à jour' : 'Enregistrer le message'}
            </Button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            <Button onClick={() => setStep(0)}>Précédent</Button>
            <Button
              type="primary"
              icon={<ClockCircleOutlined />}
              onClick={handleCreate}
              loading={creating}
              style={{ background: '#f97316', borderColor: '#f97316' }}
            >
              {scheduledAt ? 'Programmer' : 'Créer'}
            </Button>
          </div>
        )
      }
    >
      <Steps
        size="small"
        current={step}
        items={[{ title: 'Diffusion' }, { title: 'Message' }]}
        style={{ marginBottom: 20 }}
      />

      {step === 1 && messageMode === 'browse' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <Text
              strong
              style={{ fontSize: 13, marginBottom: 8, display: 'block' }}
            >
              Message
            </Text>
            <div style={{ display: 'flex', gap: 8 }}>
              <Select
                placeholder="Choisir un message existant"
                value={selectedMessageId}
                onChange={setSelectedMessageId}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                style={{ flex: 1 }}
                options={(userMessages || []).map(m => ({
                  value: m.id,
                  label: m.name || m.subject,
                }))}
              />
              <Button
                icon={<PlusOutlined />}
                onClick={() => {
                  setSelectedMessageId(null);
                  setMessageMode('new');
                }}
              >
                Nouveau message
              </Button>
            </div>
          </div>
          {(() => {
            const sel = (userMessages || []).find(
              m => m.id === selectedMessageId
            );
            if (!sel) return null;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Text
                  style={{ fontSize: 11, color: '#94a3b8', display: 'block' }}
                >
                  APERÇU — lecture seule
                </Text>
                <div
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 8,
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      padding: '8px 12px',
                      background: '#f8fafc',
                      borderBottom: '1px solid #e5e7eb',
                      fontSize: 12,
                      color: '#64748b',
                      display: 'flex',
                      gap: 16,
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>
                      <strong>Objet :</strong> {sel.subject}
                    </span>
                    {sel.from_name && (
                      <span>
                        <strong>De :</strong> {sel.from_name}
                        {sel.from_email && ` <${sel.from_email}>`}
                      </span>
                    )}
                  </div>
                  <iframe
                    srcDoc={`<style>body{font-family:Arial,sans-serif;font-size:14px;margin:8px;color:#333}p{margin:0}</style>${sel.html_content || '<p>Aucun contenu</p>'}`}
                    sandbox=""
                    title="Aperçu message"
                    style={{ width: '100%', height: 260, border: 'none', display: 'block' }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <Button
                    icon={<EditOutlined />}
                    onClick={() => setMessageMode('edit')}
                  >
                    Modifier ce message
                  </Button>
                  <Button
                    icon={<CopyOutlined />}
                    onClick={async () => {
                      try {
                        const res = await fetch(
                          `${API_URL}/messages/library/${selectedMessageId}/duplicate`,
                          { method: 'POST', headers }
                        );
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        const newMsg = await res.json();
                        message.success('Message dupliqué');
                        onMessagesChange?.();
                        if (newMsg?.id) {
                          setSelectedMessageId(newMsg.id);
                          setMessageMode('edit');
                        }
                      } catch (err) {
                        message.error(`Erreur duplication : ${err.message}`);
                      }
                    }}
                  >
                    Dupliquer et modifier
                  </Button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {step === 1 && messageMode !== 'browse' && (
        <MessageEditorDrawer
          inline
          msg={editingMsg}
          userId={user?.id}
          userSignature={userSignature}
          onSignatureUpdated={onSignatureUpdated}
          actionsRef={messageEditorActionsRef}
          onClose={() => setMessageMode('browse')}
          onSaved={savedMsg => {
            onMessagesChange?.();
            if (savedMsg?.id) setSelectedMessageId(savedMsg.id);
            setMessageMode('browse');
          }}
        />
      )}

      {step === 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Playlist */}
          <div>
            <Text
              strong
              style={{ fontSize: 13, marginBottom: 4, display: 'block' }}
            >
              Playlist
            </Text>
            <Select
              placeholder="Choisir une playlist"
              value={selectedPlaylistId}
              onChange={setSelectedPlaylistId}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              style={{ width: '100%' }}
              options={(allPlaylists || []).map(p => ({
                value: p.id,
                label: p.name,
              }))}
            />
          </div>

          {/* Nom de la campagne */}
          <div>
            <Text
              strong
              style={{ fontSize: 13, marginBottom: 4, display: 'block' }}
            >
              Nom de la campagne
            </Text>
            <Input
              value={broadcastName}
              onChange={e => setBroadcastName(e.target.value)}
              placeholder={selectedPlaylistObj?.name || 'Nom de la campagne'}
            />
          </div>

          {/* Contact count + date snapshot */}
          {selectedPlaylistId && (
            <Alert
              message={`≈ ${contactCount.toLocaleString('fr-FR')} contacts aujourd'hui — la liste sera figée au lancement (les modifs de la playlist d'ici là seront prises en compte)`}
              type="info"
              showIcon
            />
          )}

          {/* Provider */}
          <div>
            <Text
              strong
              style={{ fontSize: 12, marginBottom: 4, display: 'block' }}
            >
              Provider SMTP
            </Text>
            <Select
              placeholder="Choisir un provider"
              value={selectedProviderId}
              onChange={setSelectedProviderId}
              style={{ width: '100%' }}
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={Object.entries(providerGroups).map(
                ([server, provs]) => ({
                  label: server,
                  options: provs.map(p => ({
                    value: p.id,
                    label: `${p.name} (${p.zone || p.outbound_ip || ''})`,
                  })),
                })
              )}
            />
          </div>

          <Divider style={{ margin: '4px 0' }} />

          {/* Warmup */}
          <div>
            <Text
              strong
              style={{ fontSize: 12, marginBottom: 8, display: 'block' }}
            >
              Warmup — Estimation : {estimatedDays} jour
              {estimatedDays > 1 ? 's' : ''}
            </Text>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: 8,
              }}
            >
              <div>
                <Text style={{ fontSize: 11, color: '#64748b' }}>
                  Volume initial
                </Text>
                <InputNumber
                  value={warmupStart}
                  onChange={setWarmupStart}
                  min={1}
                  style={{ width: '100%' }}
                  size="small"
                />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#64748b' }}>Facteur</Text>
                <InputNumber
                  value={warmupFactor}
                  onChange={setWarmupFactor}
                  min={1}
                  max={3}
                  step={0.1}
                  style={{ width: '100%' }}
                  size="small"
                />
              </div>
              <div>
                <Text style={{ fontSize: 11, color: '#64748b' }}>
                  Max / jour
                </Text>
                <InputNumber
                  value={warmupMax}
                  onChange={setWarmupMax}
                  min={1}
                  style={{ width: '100%' }}
                  size="small"
                />
              </div>
            </div>
          </div>

          {/* Programmation */}
          <div>
            <Text
              strong
              style={{ fontSize: 12, marginBottom: 4, display: 'block' }}
            >
              Date de lancement
            </Text>
            <DatePicker
              value={scheduledAt}
              onChange={setScheduledAt}
              style={{ width: '100%' }}
              format="dddd D MMMM YYYY"
              locale={locale}
              disabledDate={current =>
                current && current < dayjs().endOf('day')
              }
              placeholder="Laisser vide pour créer un brouillon"
              allowClear
            />
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              {scheduledAt
                ? 'La campagne démarrera à cette date.'
                : 'Sans date, la campagne est créée en préparation.'}
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}
